// UTC day-bucket helpers shared between preflight (client scan) and
// listing (Firestore day queries). The server writes coverage.days keyed on
// UTC YYYY-MM-DD; if we ever bucketed in local time here the day counts
// would drift by up to 24h across timezones. UTC is the wire format.

const DAY_MS = 24 * 60 * 60 * 1000;

// UTC day key. Returns 'unknown' when no timestamp is available so callers
// can rely on a single sortable-ish string.
export function utcDayKey(epochMs) {
  if (!Number.isFinite(epochMs)) return 'unknown';
  const d = new Date(epochMs);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Millisecond bounds for a UTC day key (inclusive start, exclusive end).
// Returns null for the 'unknown' bucket — callers should query that bucket
// with a null-capturedAt filter instead of a range.
export function utcDayRange(dayKey) {
  if (dayKey === 'unknown' || typeof dayKey !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!match) return null;
  const start = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  if (!Number.isFinite(start)) return null;
  return { startMs: start, endMs: start + DAY_MS };
}

// Sort day keys descending by real date; 'unknown' bucket goes last.
export function sortDayKeysDesc(keys) {
  return [...keys].sort((a, b) => {
    if (a === 'unknown') return 1;
    if (b === 'unknown') return -1;
    return a < b ? 1 : a > b ? -1 : 0;
  });
}

// Human day label for section headers — e.g. "Wed, Jul 16". Uses UTC so
// the label matches the bucket (a photo taken 2am UTC Wed shouldn't render
// under "Tue" for a US viewer).
export function formatDayLabel(dayKey) {
  if (dayKey === 'unknown') return 'Unknown date';
  const range = utcDayRange(dayKey);
  if (!range) return dayKey;
  const d = new Date(range.startMs);
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
